import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { compact, sortBy } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faChevronRight, faTimes } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import { MiniSlider } from 'components/controls/MiniSlider';
import {
	getItemAlteredByScenario,
	vulnerabilityStakes
} from 'utils/rules';
import HorizontalVulnerabilityScale from 'components/Radar/HorizontalVulnerabilityScale';

import Table from 'components/Table';
import { Button, Tooltip } from 'antd';
import Weight from 'components/controls/Weight';

import dayjs from 'dayjs';
import { StakeModel } from 'model/StakeModel';

import './StakesList.sass';

interface ExtendedStakeModel extends StakeModel {
	index: number;
}

interface StakesListProps {
	// List of stakes to display
	stakes: Array<StakeModel>;

	// List of available scenarios
	scenarios: Array<{
		id: string;
		name: string;
	}>;

	// Currently selected scenario
	scenario: {
		id: string;
		name?: string;
	} | null;

	// Callback function for updating a stake
	onUpdateStake: (id: string, values: {
		trend?: string;
		weight?: number;
	}) => void;

	// Callback function for editing a stake
	onEditStake: (stake: StakeModel) => void;

	// Option to disable interactions
	visibleColumns?: {
		edit?: boolean;
		id?: boolean;
		evaluatedAt?: boolean;
		name?: boolean;
		status?: boolean;
		weight?: boolean;
	};

	readOnly?: boolean;
	showDescription?: boolean;
	selection?: any;
	deselect?: () => void;
	select?: (stake: StakeModel) => void;
}

export const StakesList = (props: StakesListProps) => {
	const { t } = useTranslation();

	const {
		stakes = [],
		visibleColumns = {
			edit: true,
			id: true,
			evaluatedAt: true,
			name: true,
			status: true,
			weight: true
		},
		readOnly = false,
		showDescription = true
	} = props;

	const allScenarios = (props.scenarios || []).concat([{ id: '', name: t('models.scenario.baseline') }]);
	let alteredStakes = sortBy(
		stakes.map((c, index) => ({
			...getItemAlteredByScenario(c, props.scenario),
			index: index,
			key: c.id || `stake-${index}` // Add a unique key for each element
		})
		),
		'name'
	) as ExtendedStakeModel[];

	let vulnerabilities = (props.scenarios || []).map((sc) => {
		return {
			id: sc.id,
			name: sc.name,
			vulnerability: vulnerabilityStakes(
				stakes.map((c) => getItemAlteredByScenario(c, sc))
			)
		};
	});

	vulnerabilities.push({
		id: '',
		name: t('models.scenario.baseline'),
		vulnerability: vulnerabilityStakes(stakes)
	});

	const selection = props.selection?.__typename === "Stake" ? stakes.find((r: any) => r.id === props.selection?.id) : null;


	const columns = compact([
		!readOnly && visibleColumns['edit'] && {
			title: '',
			dataIndex: 'edit',
			key: 'edit',
			width: 50,
			render: (text: string, record: ExtendedStakeModel) => (
				<Tooltip title='Edit'>
					<Button
						shape="circle"
						onClick={() => props.onEditStake(record)}
					>
						<FontAwesomeIcon icon={faEdit as IconDefinition} />
					</Button>
				</Tooltip>
			)
		},
		visibleColumns['id'] && {
			title: '#',
			dataIndex: 'id',
			key: 'id',
			defaultSortOrder: 'ascend',
			width: 60,
			sortable: (a: ExtendedStakeModel, b: ExtendedStakeModel) => {
				const scoreA = (a.reference || ("S" + (a.index + 1))).toLocaleLowerCase()
				const scoreB = (b.reference || ("S" + (b.index + 1))).toLocaleLowerCase()

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: string, record: ExtendedStakeModel) => {
				return record.reference || ("S" + ((record.index + 1 + "").padStart(2, '0')))
			}
		},
		visibleColumns['evaluatedAt'] && {
			title: t("models.stake.fields.evaluated_at"),
			dataIndex: 'evaluatedAt',
			key: 'evaluatedAt',
			className: 'nowrap',
			width: 140,
			sortable: (a: ExtendedStakeModel, b: ExtendedStakeModel) => {
				if (!b.evaluatedAt) return -1
				if (!a.evaluatedAt) return 1
				if (a.evaluatedAt && b.evaluatedAt && a.evaluatedAt < b.evaluatedAt) return -1;
				if (a.evaluatedAt && b.evaluatedAt > b.evaluatedAt) return 1;
				return 0
			},
			render: (text: string, record: ExtendedStakeModel, index: number) => {
				return <Tooltip title={record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY HH[h]mm') : ''}>
					<div>
						{record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY') : ''}
					</div>
				</Tooltip>
			}
		},
		visibleColumns['name'] && {
			title: t('models.stake.fields.name'),
			dataIndex: 'name',
			key: 'name',
			className: 'w-50',
			sortable: true,
			render: (text: string, record: ExtendedStakeModel) => {
				return (
					<><span className={[
						"name cursor-pointer",
						record.id === selection?.id ? "selected" : ""
					].join(" ")}
						onClick={() => record.id === selection?.id ? props.deselect && props.deselect() : props.select && props.select(record)}>
						{record.name}

					</span>
						{showDescription && record.description ? (
							<div onClick={() => props.select && props.select(record)} className="description">
								{record.description}
							</div>
						) : (
							''
						)}</>
				);
			}
		},
		visibleColumns['status'] && {
			title: 'Status',
			dataIndex: 'trend',
			key: 'trend',
			width: 200,
			sortable: (a: ExtendedStakeModel, b: ExtendedStakeModel) => {
				const scoreA = parseInt(a.trend)
				const scoreB = parseInt(b.trend)

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: string, record: ExtendedStakeModel) => {
				return (
					<MiniSlider
						className="mr-2"
						key={record.id}
						value={record.trend}
						min={0}
						max={100}
						style={{ width: '100%', flexShrink: 0 }}
						onChange={(val: string) =>
							props.onUpdateStake(record.id, { trend: val })
						}
						readOnly={readOnly}
						additionalMarkers={(allScenarios).map((sc) => {
							return {
								id: sc.id,
								name: sc.name || t("models.scenario.baseline"),
								value: getItemAlteredByScenario(record, sc).trend
							};
						})}
					/>
				);
			}
		},
		visibleColumns['weight'] && {
			title: t('models.stake.fields.weight'),
			dataIndex: 'weight',
			key: 'weight',
			sortable: true,
			width: 100,
			render: (text: string, record: ExtendedStakeModel) => {
				const currentWeight = record.weight || 1;
				return (
					<Weight
						style={{ marginRight: '0.5rem' }}
						disabled={readOnly}
						onClick={(values: any) => {
							if (readOnly) return;
							let newWeight = currentWeight + 1;
							if (newWeight > 3) newWeight = 0;
							props.onUpdateStake(record.id, {
								...record,
								weight: newWeight
							});
						}}
					>
						{currentWeight}
					</Weight>
				);
			}
		},
		{
			title: '',
			dataIndex: 'select',
			key: 'select',
			width: 50,
			render: (text: string, record: ExtendedStakeModel) => (
				selection?.id !== record.id ? (
					<Tooltip title={t('actions.view')} placement='left'>
						<Button
							shape="circle"
							className={selection ? "discreet" : ""}
							onClick={() => props.select && props.select(record)}
						>
							<FontAwesomeIcon icon={faChevronRight as IconDefinition} />
						</Button>
					</Tooltip>
				) : (
					<Tooltip title={t('actions.close')} placement='left'>
						<Button
							shape="circle"
							onClick={() => props.deselect && props.deselect()}
						>
							<FontAwesomeIcon icon={faTimes as IconDefinition} />
						</Button>
					</Tooltip>
				)
			)
		}
	]);

	return (
		<div className="StakesList d-flex w-100 animation-fade-in">
			<HorizontalVulnerabilityScale
				vulnerabilities={vulnerabilities}
				scenarios={props.scenarios}
				scenario={props.scenario}
			/>

			<div className="mb-3 mt-1 w-100 pr-2 pl-2" style={{ maxWidth: '100%' }}>
				<div className="scroll-container" style={{ overflowY: 'auto', width: '100%' }}>
					<Table
						pagination={false}
						dataSource={alteredStakes}
						defaultSortOrder={["ascend"]}
						columns={columns}
						size="small"
						rowKey="id"
						className="mr-1"
						onRow={(record: any) => ({
							'data-testid': `stake-list-item-${record.id}`,
							onDoubleClick: () => props.onEditStake(record),
						})}
					/>
				</div>
			</div>
		</div>
	);
};
